import { useCallback, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../../../../store";
import { DesktopTable } from "./DesktopTable/DesktopTable";
import { ActionReportTableLoader } from "./DesktopTable/DesktopTableLoader";
import { ActionReportMobileCardLoader, MobileTable } from "./MobileTable/MobileTable";
import { ActionReportRow, FieldConfig } from "./types";
import { showProfile, showCustomerProfile } from "../../../../reducers/profile";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useLocation } from "react-router-dom";
import { trackOpenDownlineProfile } from "../../../DownlineProfile/downlineProfileTealium";
export type { ActionReportRow, ActionReportListProps } from "./types";

export type BaseABOData = {
  [key: string]: string | string[] | boolean | number | undefined;
  abo: string;
  aff: string;
  aboName: string;
  aboNumber: string;
  isProvisional?: boolean;
  isInternational?: boolean;
  countryCode?: string;
  isFirstContractYear?: boolean;
  phoneNumber?: string;
  whatsapp?: string;
  isConfidential?: boolean;
  isCustomer?: boolean;
  /** Trusted report policy marker used for customer names and profile privacy. */
  allowCustomerDetails?: boolean;
  businessNature?: string;
  businessNatureTag?: "abo" | "member" | "customer" | "guestCustomer";
  level?: number;
  imageRefUrl?: string;
  conversionType?: string;
};

type Props = {
  selectedKpis: string[];
  reportId: string;
  reportData: BaseABOData[];
  fieldConfig: FieldConfig[];
  enableWatchlist?: boolean;
  batchSize?: number;
  aboHeader?: string;
  period?: string;
  skipEnrichment?: boolean;
  enrichWhatsApp?: boolean;
  awaitAutoEnrichment?: boolean;
  scrollToIndex?: number;
  trackABONameClick?: Function;
  onProfileTrack?: () => void;
  onWatchlistTrack?: (isAdding: boolean) => void;
  onPhoneTrack?: () => void;
  onMessageTrack?: () => void;
  onColumnHeaderClick?: (kpi: string) => void;
  columnSortState?: Record<string, "asc" | "desc" | undefined>;
  showImageBlock?: boolean;
  mobileCardStyle?: "standard" | "no-divider";
  showTagForDeepCustomers?: boolean;
  requestWithSponsorForCustomers?: boolean;
};

export const ActionReportListViewLoader = ({ columns }: { columns?: number } = {}) => {
  const isMobile = useIsMobile();
  return isMobile ? (
    <>
      <ActionReportMobileCardLoader numRows={columns} />
      <ActionReportMobileCardLoader numRows={columns} />
      <ActionReportMobileCardLoader numRows={columns} />
    </>
  ) : (
    <ActionReportTableLoader columns={columns} />
  );
};

export const ActionReportListView = ({
  selectedKpis,
  reportId,
  reportData,
  enableWatchlist,
  batchSize,
  fieldConfig,
  aboHeader = "aboNameNumber",
  period: periodProp,
  skipEnrichment = false,
  enrichWhatsApp = false,
  awaitAutoEnrichment = false,
  scrollToIndex,
  trackABONameClick = () => {},
  onProfileTrack,
  onWatchlistTrack,
  onPhoneTrack,
  onMessageTrack,
  onColumnHeaderClick,
  columnSortState,
  showImageBlock = false,
  mobileCardStyle = "standard",
  showTagForDeepCustomers = false,
  requestWithSponsorForCustomers = false,
}: Props) => {
  const isMobile = useIsMobile();
  const { selectedPeriod } = useAppSelector((state) => state.period);
  const period = periodProp ?? selectedPeriod;
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const { profile } = useAppSelector((store) => store.boot.configuration);
  const isEnabled = profile?.enableDownlineProfile ?? false;
  const profileParam = new URLSearchParams(window.location.search).get("profile");
  const isLegacyOverride = profileParam === "legacy";
  const useDownlineProfile = (isEnabled || profileParam === "downline") && !isLegacyOverride;
  const showABOProfile = useCallback(
    (event: CustomEventInit) => {
      const { affAbo, isCustomer, isConfidential, level, allowCustomerDetails } = event.detail.abo;
      const isNewCustomer = isCustomer && Number(level) > 2 && !showTagForDeepCustomers;
      if (isCustomer) {
        const confidentialView = isConfidential || (isNewCustomer && !allowCustomerDetails);
        dispatch(
          showCustomerProfile({
            affAbo,
            confidentialView,
            isConfidentialCustomer: isConfidential === true,
            requestWithSponsor: requestWithSponsorForCustomers,
          }),
        );
      } else {
        dispatch(showProfile({ affAbo, isFromActionReport: true }));
        if (useDownlineProfile) {
          trackOpenDownlineProfile(pathname);
        }
      }
    },
    [dispatch, pathname, useDownlineProfile, showTagForDeepCustomers, requestWithSponsorForCustomers],
  );

  const rows: ActionReportRow[] = useMemo(() => (reportData || []).map((record) => ({ ...record })), [reportData]);

  const commonProps = useMemo(
    () => ({
      rows,
      selectedKpis,
      enableWatchlist,
      reportId,
      period,
      batchSize,
      fieldConfig,
      showABOProfile,
      aboHeader,
      skipEnrichment,
      enrichWhatsApp,
      awaitAutoEnrichment,
      scrollToIndex,
      trackABONameClick,
      onProfileTrack,
      onWatchlistTrack,
      onPhoneTrack,
      onMessageTrack,
      onColumnHeaderClick,
      columnSortState,
      showImageBlock,
      mobileCardStyle,
      showTagForDeepCustomers,
    }),
    [
      rows,
      selectedKpis,
      enableWatchlist,
      reportId,
      period,
      batchSize,
      fieldConfig,
      showABOProfile,
      aboHeader,
      skipEnrichment,
      enrichWhatsApp,
      awaitAutoEnrichment,
      scrollToIndex,
      trackABONameClick,
      onProfileTrack,
      onWatchlistTrack,
      onPhoneTrack,
      onMessageTrack,
      onColumnHeaderClick,
      columnSortState,
      showImageBlock,
      mobileCardStyle,
      showTagForDeepCustomers,
    ],
  );

  return isMobile ? <MobileTable {...commonProps} /> : <DesktopTable {...commonProps} />;
};
